import React, { memo, useState, useCallback } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { haptic } from '../../../shared/utils/haptics';

/**
 * LoginButton - Styled Google Login component
 */
export const LoginButton = memo(function LoginButton({
  onSuccess,
  onError,
  buttonText = 'Continuer avec Google',
  size = 'default'
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSuccess = useCallback((credentialResponse) => {
    setIsLoading(false);
    setStatus('success');
    haptic.success();

    try {
      const details = jwtDecode(credentialResponse.credential);
      console.log('Utilisateur connecté:', details);

      if (onSuccess) {
        onSuccess(details, credentialResponse);
      }

      setTimeout(() => setStatus(null), 2000);
    } catch (error) {
      console.error('Erreur décodage JWT:', error);
      setStatus('error');
      haptic.error();
      if (onError) {
        onError(error);
      }
    }
  }, [onSuccess, onError]);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setStatus('error');
    haptic.error();
    console.log('La connexion a échoué');

    if (onError) {
      onError(new Error('Connexion Google échouée'));
    }

    setTimeout(() => setStatus(null), 3000);
  }, [onError]);

  return (
    <div className="relative">
      <div className={`google-login-wrapper ${size === 'compact' ? 'compact' : ''}`}>
        <GoogleLogin
          onSuccess={handleSuccess}
          onError={handleError}
          theme="filled_black"
          size={size === 'compact' ? 'medium' : 'large'}
          text="continue_with"
          shape="rectangular"
          logo_alignment="left"
          useOneTap={false}
        />
      </div>

      <AnimatePresence>
        {status && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center rounded-xl glass-card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {status === 'success' ? (
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Connecté</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Erreur</span>
              </div>
            )}
          </motion.div>
        )}

        {isLoading && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center rounded-xl glass-card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Loader2 className="w-5 h-5 text-zinc-400 animate-spin" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

/**
 * CustomGoogleButton - Alternative styled button
 */
export const CustomGoogleButton = memo(function CustomGoogleButton({
  onClick,
  isLoading = false,
  disabled = false,
  children = 'Continuer avec Google'
}) {
  const handleClick = useCallback(() => {
    haptic.tap();
    onClick?.();
  }, [onClick]);

  return (
    <motion.button
      onClick={handleClick}
      disabled={disabled || isLoading}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white hover:bg-zinc-100 text-zinc-900 rounded-xl font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      whileHover={!disabled && !isLoading ? { scale: 1.01 } : {}}
      whileTap={!disabled && !isLoading ? { scale: 0.99 } : {}}
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <>
          <img
            src="https://www.google.com/favicon.ico"
            alt="Google"
            className="w-4 h-4"
          />
          {children}
        </>
      )}
    </motion.button>
  );
});

/**
 * GoogleIconButton - Minimal icon button
 */
export const GoogleIconButton = memo(function GoogleIconButton({
  onClick,
  isLoading = false,
  size = 'default'
}) {
  const handleClick = useCallback(() => {
    haptic.tap();
    onClick?.();
  }, [onClick]);

  const sizeClasses = {
    default: 'w-10 h-10',
    small: 'w-8 h-8'
  };

  return (
    <motion.button
      onClick={handleClick}
      disabled={isLoading}
      className={`${sizeClasses[size]} flex items-center justify-center bg-white hover:bg-zinc-100 rounded-lg transition-all disabled:opacity-50`}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 text-zinc-600 animate-spin" />
      ) : (
        <img
          src="https://www.google.com/favicon.ico"
          alt="Google"
          className="w-4 h-4"
        />
      )}
    </motion.button>
  );
});

export default LoginButton;
